import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Link, Stack, Typography } from "@mui/joy";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import {
    ApiBounds,
    ApiConnection,
    ApiElementsResponse,
    ApiFile,
    ApiFragmentNode,
} from "../../shared/interfaces";

// @ts-ignore
import * as Addons from "@foxitsoftware/foxit-pdf-sdk-for-web-library/lib/uix-addons/allInOne.js";
// @ts-ignore
import * as mobileAddons from "@foxitsoftware/foxit-pdf-sdk-for-web-library/lib/uix-addons/allInOne.mobile.js";

import * as UIExtension from "@foxitsoftware/foxit-pdf-sdk-for-web-library/lib/UIExtension.full.js";
import "@foxitsoftware/foxit-pdf-sdk-for-web-library/lib/UIExtension.vw.css";

import "./FileViewer.css";
import { useDebounce } from "use-debounce";
import { useQuery } from "react-query";
import { PrimeQueryKeys } from "../../shared/constants";
import { FileApi } from "../../api/file";

import { renderAllAnnotations, setAnnotationActive } from "./annotationRenderer";
import { makeBoundsVisible } from "./scroller";
import { PrimeDebugLog } from "../../util/debugLog";
import { loadFileAndCache } from "./loadFileAndCache";
import FileLoadProgress from "./FileLoadProgress";
import { useMergedQueryData } from "../../hooks/useMergedQueryData";
import { getIdForBounds } from "../../util/objectUtil";
import { useJumpToConnectionWithHistory } from "./useJumpToConnectionWithHistory";

export type FileViewerArgs = {
    file: ApiFile;
    referenceHighlights?: ApiBounds[] | undefined;
    scrollToFragmentNode?: ApiFragmentNode | null;
    selectedFragmentNodeId?: string | null;
};

export type FileViewerProps = {
    fileArgs: FileViewerArgs;
    showTitleAndClose: boolean;
    showSidebar?: boolean;
    onClose?: () => void;
    onFragmentNodeSelected?: (fragmentNode: ApiFragmentNode | null) => void;
};

const styles = {
    titleContainer: { px: 2 },
    pdfContainer: {
        display: "block",
        width: "100%",
        height: "100%",
    },
};

const licenseSN = "EvR5iolxRN2ogQM/EdiKZZcv9eCnDjpFkaY2jQZhUUSynQvsJWXJkw==";
const licenseKey =
    "PjwcmDSwMV6sbxteo9fUzPzlYY+UQvZ0R7AacfiJZ1ArjKPoO8q+UbeIAlbnp2nPY5EM5nRKOToOreHFZQWQ0ZcP9fLOC6pZ4K9qalbz/tpe8Y31Du0Hh2gIHWPnHwLnWDMLxw0WvVya210b4UDe/Y+t7Llt7tN+agLCydU4E9dJ8NgRLXDa2zNZpfhL2anMX/VlRQE+hxds36mAeGWDKnUE1be8iOMddyOL5sotgVnL+uqtr6AINsfzDg790x0v8Qxe6yeWaNnvTLPDtR/DDeBE6lyzy9MVcWr9Vps298HxxBFF1rz1rYHpGMzIC3dDLHfL4Qjzhox/QBUGepnriewd8rhX5uq6niZl1zBQx0V4hjBwI1ieUF2UjWQR5pBfnStRgnaGfpwVyX/sT39jxJEENA09/3xVDnieVCl5ulF29sGvHn7RNHQZ2xNCoHecpVS7sCYw0LHjAJ9YcQ1eUsfZQ2iTex1dxrTH71+vMLtPq65UY5B6JMCNhxXz92xibuid2KobDoDOUdJC87oSVq5Ony4S1s+dZ0iCIy1WfkTLyBdG+i83xDz/tprIDTDDdQPzNCZ3HAB4nBjtFpARSnGYSsGsVhJv1c1E2AY+OmY9GoNGXDa+udwDtieDgQNtbL1e9m+k/YhG2WWh4QMVO1W1xwyWhHgGZsQdsdFsDcAkItqKOniAwZw7B5GHmJbR416dAL4XFX1Wt8Vi89nQFQ7TOqHDKKN28SZdecjOwZMyONlQ8bavhzqxVBjBoFAS2ThHXJFkFQqg32gdWJmg0q/cuIWPVvUk1fwf8+M3vGCwNg0vaDp5P5jsflqN0xj8Rd3CI4XkECDFHNdq+X5OYX6MQdIFbOyr5lE6oE0IsTw2KD+OXDp+mV+/cBiFFw5W5H7sFQWlQ32zlKSaYL2Ug2qLyt5myP13WWdHAPeNaUOt2LxHvL2y8mol67D5chVQ1Wz/G12Y9ACN8OyVL7S3rn3z65fYmADwm2ZRd5zDw6FgA5h7ZS+HJyBeNJYtslTBlGFBD2E6up1zBQMMiY0diESmG3168cD/XJT94B5bHqPBcD2mpGcBFOZvpTFa9WXujMvlefhkdy1WgWNFW1hsYueprDdTEgshy/olV1wyX1k7IIosTDoQET8r2aU/GUyTmnuIcM0pI9HUDea13W8WbiqcGfqBxFPNGr4pCkcRycHus6KhiUB7rtbj/Wg7Dq4VpKMoWFN7CQMbZ1PC7A9S8zX34n5azUxvIhO6iBOLruhpaOE=";

// Foxit has a lot stuff in it. This a list of stuff we don't want. NB -- we don't want the layers button either,
// but removing that causes the viewer to crash.
const FoxitComponentsToRemove = [
    "download-file-button",
    "snapshot-button",
    "open-file-button-list",
    "file-property",
    "home-tab-group-hand",
    "home-tab-group-io",
    "selection-dropdown",
    "change-color-dropdown",
    "home-tab-group-page",
    "protect-tab",
    "form-tab",
    "view-tab",
    "comment-tab",
    "edit-tab",
    "home-tab",
    "comment-list-sidebar-panel",
    "sidebar-attachment",
    "sidebar-field",
];

export default function FileViewer(props: FileViewerProps) {
    const { fileArgs, onFragmentNodeSelected, showTitleAndClose, showSidebar, onClose } = props;

    const pdfUiRef = useRef<UIExtension.PDFUI | null>(null);

    // Track the immediate page; debounce to avoid spamming requests
    const [currentPage, setCurrentPage] = useState(0);
    const [debouncedCurrentPage] = useDebounce(currentPage, 250);

    const [loadProgress, setLoadProgress] = useState<{
        loadedBytes: number;
        totalBytes: number;
    }>({
        loadedBytes: -1,
        totalBytes: -1,
    });

    const fileRef = useRef<ApiFile | null>(null);
    const refHighlightsRef = useRef<ApiBounds[]>([]);
    const fragmentNodesRef = useRef<ApiFragmentNode[]>([]);
    const connectionsRef = useRef<ApiConnection[]>([]);
    const scrollToFragmentNodeRef = useRef<ApiFragmentNode | null>(null);
    const fileLoadedRef = useRef(false);

    const [selectedFragmentNodeId, setSelectedFragmentNodeId] = React.useState<string | null>(null);

    // Fragment nodes query
    const elementsQuery = useQuery<ApiElementsResponse>(
        PrimeQueryKeys.fileElements(fileArgs.file.id, debouncedCurrentPage),
        () => FileApi.getFileElements(fileArgs.file.id, debouncedCurrentPage),
        {
            keepPreviousData: true,
        },
    );

    const fragmentNodes = useMergedQueryData<ApiFragmentNode>(
        elementsQuery.data,
        fileArgs.file.id,
        "fragment_nodes",
        (node) => node.id,
        (node) => node.fileId,
    );

    const connections = useMergedQueryData<ApiConnection>(
        elementsQuery.data,
        fileArgs.file.id,
        "connections",
        (conn) => getIdForBounds(conn.srcBounds),
        (conn) => conn.srcFileId,
    );

    const filteredFragmentNodes = useMemo(() => fragmentNodes, [fragmentNodes]);
    const filteredConnections = useMemo(() => connections, [connections]);

    const jumpToFirstReferenceOrSelectedFragmentNode = useCallback(async () => {
        PrimeDebugLog(
            "FileViewer",
            `jumpToFirstReferenceOrSelectedFragmentNode | fileLoadedRef ${fileLoadedRef.current}`,
            refHighlightsRef.current,
            scrollToFragmentNodeRef.current,
        );
        if (!pdfUiRef.current || !fileLoadedRef.current) {
            return;
        }

        // HACK(hp) - Don't adjust zoom on drawings
        const shouldAdjustZoom = fileArgs.file?.fileName.toLowerCase().indexOf("drawing") !== -1;

        if (refHighlightsRef.current?.length > 0) {
            // Have a reference highlight - scroll to it
            await makeBoundsVisible(
                pdfUiRef.current,
                refHighlightsRef.current[0],
                shouldAdjustZoom,
                refHighlightsRef.current.length > 1,
            );
        } else if (scrollToFragmentNodeRef.current) {
            await makeBoundsVisible(
                pdfUiRef.current,
                scrollToFragmentNodeRef.current.bounds,
                shouldAdjustZoom,
                false,
            );
        }
    }, [fileArgs.file?.fileName]);

    const handleFragmentNodeClick = useCallback(
        (fragmentNode: ApiFragmentNode) => {
            PrimeDebugLog("FileViewer", `handleFragmentNodeClick`);
            onFragmentNodeSelected?.(fragmentNode);
        },
        [onFragmentNodeSelected],
    );

    const jumpToBounds = useCallback(async (dst: ApiBounds) => {
        refHighlightsRef.current = [dst];
        await makeBoundsVisible(pdfUiRef.current!, dst, true, false);
    }, []);
    const { handleConnectionClick } = useJumpToConnectionWithHistory({
        pdfUiRef,
        fileId: props.fileArgs.file.id,
        fileLoaded: fileLoadedRef.current,
        jumpToBounds,
    });

    const doRenderAnnotations = useCallback(() => {
        PrimeDebugLog("FileViewer", `doRenderAnnotations`);
        renderAllAnnotations({
            refHighlights: refHighlightsRef.current,
            fragmentNodes: fragmentNodesRef.current,
            connections: connectionsRef.current,
            selectedFragmentNodeId,
            handleFragmentNodeClick,
            handleConnectionClick,
        });
    }, [selectedFragmentNodeId, handleFragmentNodeClick, handleConnectionClick]);

    const handlePageNumberChange = useCallback(
        async (e: number) => {
            PrimeDebugLog("FileViewer", `pageNumberChange ${e}`);
            setCurrentPage(e - 1);
        },
        [setCurrentPage],
    );

    const handleRenderFileSuccess = useCallback(async () => {
        fileLoadedRef.current = true;

        const viewModeManager = await pdfUiRef.current?.getViewModeManager();

        // @ts-ignore
        const viewMode = viewModeManager?.getCurrentViewMode().getName();
        PrimeDebugLog("FileViewer", `renderFileSuccess | view mode ${viewMode}`);

        // With large documents Foxit will sometimes change the view mode. If it did this, change it back and then
        // render the annotations. Otherwise, render them immediately.
        doRenderAnnotations();
        jumpToFirstReferenceOrSelectedFragmentNode();
    }, [jumpToFirstReferenceOrSelectedFragmentNode, doRenderAnnotations]);

    const handleRenderFileFailure = useCallback((e: any) => {
        PrimeDebugLog("FileViewer", "render file failure", e);
    }, []);

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.ctrlKey || e.altKey || e.shiftKey) {
            e.stopImmediatePropagation();
        }
    }, []);

    const handleChangeViewModeSuccess = useCallback(
        async (e: any) => {
            PrimeDebugLog("FileViewer", `handleChangeViewModeSuccess`);
            doRenderAnnotations();
            await jumpToFirstReferenceOrSelectedFragmentNode();
        },
        [jumpToFirstReferenceOrSelectedFragmentNode, doRenderAnnotations],
    );

    useEffect(() => {
        PrimeDebugLog("FileViewer", "init");
        if (pdfUiRef.current) {
            return;
        }

        PrimeDebugLog("FileViewer", "initializing pdfUiRef", pdfUiRef.current);
        pdfUiRef.current = new UIExtension.PDFUI({
            viewerOptions: {
                libPath: "/foxit-lib",
                jr: {
                    enginePath: "/foxit-lib/jr-engine/gsdk",
                    fontPath: "http://webpdf.foxitsoftware.com/webfonts/",
                    licenseKey: licenseKey,
                    licenseSN: licenseSN,
                },
                defaultScale: "fitWidth",
                viewModes: ["continuous-view-mode"],
                messageSyncServiceWorker: {
                    url: "/foxit-lib/MessageWorker.js",
                    options: {
                        scope: "/foxit-lib/",
                    },
                },
            },
            appearance: UIExtension.appearances.ribbon.extend({
                getDefaultFragments: function () {
                    return FoxitComponentsToRemove.map((target) => ({
                        target,
                        action: UIExtension.UIConsts.FRAGMENT_ACTION.REMOVE,
                    }));
                },
            }),
            renderTo: "#pdf-ui",
            addons: UIExtension.PDFViewCtrl.DeviceInfo.isMobile ? mobileAddons : Addons,
        });

        if (showSidebar) {
            pdfUiRef.current?.getAllComponentsByName("sidebar-bookmark-v2").then((c) => {
                if (c.length > 0) {
                    c[0].active();
                }
            });
        }

        // HACK(hp) - On large files Foxit will switch to a single page mode messing everything up. This stops that.
        pdfUiRef.current.getViewModeManager().then((vm) => {
            vm.switchTo = (name: string) => {
                PrimeDebugLog("FileViewer", `switching to ${name} - not allowing`);
            };
        });

        pdfUiRef.current.addViewerEventListener(
            // @ts-ignore
            UIExtension.PDFViewCtrl.ViewerEvents.pageNumberChange,
            handlePageNumberChange,
        );

        pdfUiRef.current.addViewerEventListener(
            // @ts-ignore
            UIExtension.PDFViewCtrl.ViewerEvents.renderFileSuccess,
            handleRenderFileSuccess,
        );

        pdfUiRef.current.addViewerEventListener(
            // @ts-ignore
            UIExtension.PDFViewCtrl.ViewerEvents.renderFileFailed,
            handleRenderFileFailure,
        );

        pdfUiRef.current.addViewerEventListener(
            // @ts-ignore
            UIExtension.PDFViewCtrl.ViewerEvents.changeViewModeSuccess,
            handleChangeViewModeSuccess,
        );

        // Hackety hack -- intercept key events to stop Foxit from handling them
        document.addEventListener("keydown", handleKeyDown, true);

        return () => {
            PrimeDebugLog("FileViewer", `destroying pdfUiRef have ref ${pdfUiRef.current}`);
            if (!pdfUiRef.current) {
                return;
            }

            pdfUiRef.current.removeViewerEventListener(
                // @ts-ignore
                UIExtension.PDFViewCtrl.ViewerEvents.pageNumberChange,
                handlePageNumberChange,
            );

            pdfUiRef.current.removeViewerEventListener(
                // @ts-ignore
                UIExtension.PDFViewCtrl.ViewerEvents.renderFileSuccess,
                handleRenderFileSuccess,
            );

            pdfUiRef.current.removeViewerEventListener(
                // @ts-ignore
                UIExtension.PDFViewCtrl.ViewerEvents.renderFileFailed,
                handleRenderFileFailure,
            );

            pdfUiRef.current.removeViewerEventListener(
                // @ts-ignore
                UIExtension.PDFViewCtrl.ViewerEvents.changeViewModeSuccess,
                handleChangeViewModeSuccess,
            );

            document.removeEventListener("keydown", handleKeyDown, true);

            try {
                pdfUiRef.current.destroy();
                pdfUiRef.current = null;
            } catch (error) {
                console.error("Error destroying PDF UI:", error);
            }

            fileLoadedRef.current = false;
            PrimeDebugLog("FileViewer", "destroy done");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Only once per component mount
    }, []);

    useEffect(() => {
        fragmentNodesRef.current = filteredFragmentNodes;
        connectionsRef.current = filteredConnections;

        if (!pdfUiRef.current || !fileLoadedRef.current) {
            return;
        }

        PrimeDebugLog(
            "FileViewer",
            `rendering annotations | fragments=${fragmentNodesRef.current.length} connections=${connectionsRef.current.length}`,
        );

        doRenderAnnotations();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- doRenderAnnotations is stable
    }, [filteredFragmentNodes, filteredConnections, fileArgs.file.id]);

    useEffect(() => {
        PrimeDebugLog(
            "FileViewer",
            "referenceHighlight change rendering",
            fileArgs.referenceHighlights,
        );
        refHighlightsRef.current = fileArgs.referenceHighlights ?? [];

        if (!pdfUiRef.current) {
            return;
        }

        jumpToFirstReferenceOrSelectedFragmentNode().then(() => {
            PrimeDebugLog("FileViewer", "jumpToFirstReferenceHighlight done");
            doRenderAnnotations();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileArgs.referenceHighlights]);

    useEffect(() => {
        PrimeDebugLog(
            "FileViewer",
            "FileViewer",
            "scrollToFragmentNode change rendering",
            fileArgs.scrollToFragmentNode,
        );
        scrollToFragmentNodeRef.current = fileArgs.scrollToFragmentNode ?? null;

        if (!pdfUiRef.current || !fileArgs.scrollToFragmentNode) {
            return;
        }

        jumpToFirstReferenceOrSelectedFragmentNode().then(() => {
            doRenderAnnotations();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileArgs.scrollToFragmentNode]);

    useEffect(() => {
        PrimeDebugLog(
            "FileViewer",
            "FileViewer",
            "selectedFragmentNodeId change rendering",
            fileArgs.selectedFragmentNodeId,
        );
        setSelectedFragmentNodeId(fileArgs.selectedFragmentNodeId ?? null);
        doRenderAnnotations();
    }, [fileArgs.selectedFragmentNodeId, doRenderAnnotations]);

    useEffect(() => {
        const controller = new AbortController();
        let mounted = true;

        const loadPdf = async () => {
            try {
                if (!pdfUiRef.current || !mounted) {
                    return;
                }

                // Skip if it’s the same file
                if (fileRef.current?.fileUrl === fileArgs.file.fileUrl) {
                    return;
                }

                fileRef.current = fileArgs.file;

                const file = await loadFileAndCache(
                    fileArgs.file,
                    (loadedBytes, totalBytes) => {
                        if (mounted) {
                            setLoadProgress({ loadedBytes, totalBytes });
                        }
                    },
                    { signal: controller.signal },
                );

                if (!mounted) {
                    return;
                }

                if (file) {
                    await pdfUiRef.current.openPDFByFile(file, {
                        fileName: fileArgs.file.fileName,
                    });
                } else {
                    // Fallback loader -- should never happen but keep it just in case
                    await pdfUiRef.current.openPDFByHttpRangeRequest(
                        {
                            range: {
                                url: fileRef.current.fileUrl,
                                chunkSize: 1024 * 1024,
                            },
                        },
                        { fileName: fileRef.current.fileName },
                    );
                }
            } catch (e) {
                PrimeDebugLog("FileViewer", "Failed loading file", e);
            }
        };

        loadPdf();

        return () => {
            mounted = false;
            controller.abort();
        };
    }, [fileArgs.file]);

    useEffect(() => {
        PrimeDebugLog("FileViewer", "selectedFragmentNodeId changed to: ", selectedFragmentNodeId);
        setAnnotationActive(selectedFragmentNodeId);
    }, [selectedFragmentNodeId]);

    return (
        <React.Fragment>
            {showTitleAndClose && (
                <Stack direction="row" justifyContent="space-between" sx={styles.titleContainer}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Link
                            href={`/doc/${props.fileArgs.file.id}`}
                            endDecorator={<OpenInNewOutlinedIcon />}
                            color="neutral"
                            level="title-md"
                            target="_blank"
                        >
                            <Typography level="title-md" noWrap>
                                {fileArgs.file.fileName}
                            </Typography>
                        </Link>
                    </Stack>

                    <IconButton
                        aria-label="Close"
                        onClick={(e) => {
                            e.preventDefault();
                            onClose?.();
                        }}
                    >
                        <CloseOutlinedIcon />
                    </IconButton>
                </Stack>
            )}

            <Box sx={styles.pdfContainer} id="pdf-ui" />

            <FileLoadProgress
                loadedBytes={loadProgress.loadedBytes}
                totalBytes={loadProgress.totalBytes}
            />
        </React.Fragment>
    );
}
